import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import "react-datepicker/dist/react-datepicker.css";

import { UserAuth } from '../auth/Auth';
import { PrivateAxios } from '../../environment/AxiosInstance';
import { ErrorMessage, SuccessMessage } from '../../environment/ToastMessage';

const EditMiniProjectModal = ({ show, handleClose, project, onUpdated }) => {
  const { User, MatchPermission } = UserAuth();
  
  // ================== ALL HOOKS MUST BE AT THE TOP ==================
  const [projectName, setProjectName] = useState("");
  const [projectHeads, setProjectHeads] = useState([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  const [startDate, endDate] = dateRange;

  // Theme Observer
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkTheme(document.body.classList.contains("dark-theme"));
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Populate fields useEffect
  useEffect(() => {
    if (!project) return;
    setProjectName(project.project_name || "");
    setDescription(project.description || "");
    
    // Populate project heads
    if (project.project_head_ids) {
      const headIds = Array.isArray(project.project_head_ids) 
        ? project.project_head_ids 
        : JSON.parse(project.project_head_ids || '[]');
      const selectedHeads = User?.filter(u => headIds.includes(u.id)) || [];
      setProjectHeads(selectedHeads);
    }
    
    // Populate date range
    setDateRange([
      project.start_date ? new Date(project.start_date) : null,
      project.end_date ? new Date(project.end_date) : null
    ]);
  }, [project, User]);

  // ================== PERMISSION CHECK AFTER ALL HOOKS ==================
  // Only allow users with Edit Project permission
  if (!MatchPermission(['Edit Project', 'Project All'])) {
    return null;
  }

  const handleUpdateProject = async () => {
    if (!MatchPermission(['Edit Project', 'Project All'])) {
      ErrorMessage("You don't have permission to edit projects");
      return;
    }

    if (!projectName || !projectHeads.length) {
      ErrorMessage("Please fill all required fields!");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        project_name: projectName,
        description,
        start_date: startDate ? startDate.toISOString() : null,
        end_date: endDate ? endDate.toISOString() : null,
        project_head_ids: projectHeads.map(u => u.id),
      };

      const res = await PrivateAxios.put(`/project/update/${project.id}`, payload);

      if (res.data.success) {
        SuccessMessage("Project updated successfully!");
        handleClose();
        if (onUpdated) onUpdated();
      } else {
        ErrorMessage(res.data.message || "Failed to update project");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        ErrorMessage("Session expired. Please log in again.");
      } else {
        ErrorMessage(err.response?.data?.message || "Server error!");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal id="EditProjectModal" show={show} onHide={handleClose} backdrop="static" centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="gth-modal-title">Edit Project</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-1">
        <div className="row">
          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label">Project Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
          </div>

          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Project Head</label>
              <div className="custom-select-wrap">
                <Select
                  isMulti
                  options={User}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={projectHeads}
                  onChange={(selected) => setProjectHeads(selected)}
                  theme={(theme) => ({
                    ...theme,
                    colors: { ...theme.colors, primary25: '#ddddff', primary: '#6161ff' },
                  })}
                  styles={{
                    menu: (base) => ({ ...base, backgroundColor: isDarkTheme ? "#06162f" : "#ffffff" }),
                    singleValue: (base) => ({ ...base, color: isDarkTheme ? "#e0e0e0" : "#000000" }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isFocused ? (isDarkTheme ? "#112a46" : "#ddddff") : "transparent",
                      color: isDarkTheme ? "#e0e0e0" : "#000000",
                    }),
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="form-group">
              <label className="form-label">Project TimeLine</label>
              <div className="exp-datepicker-cont">
                <span className="cal-icon"><i className="fas fa-calendar-alt"></i></span>
                <DatePicker
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  isClearable={false}
                  placeholderText={startDate && endDate ? "" : "Select Date"}
                />
              </div>
            </div>
          </div>

          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="gth-blue-light-bg">
        <button type="button" className="btn" onClick={handleClose}>Cancel</button>
        <button type="button" className="btn btn-exp-green" onClick={handleUpdateProject} disabled={loading}>
          {loading ? "Updating..." : "Update"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditMiniProjectModal;
